/**
 * WinningStrategy — "Claire's Winning Strategy" panel (Claude-powered).
 * Purple CTA inside lead detail modals (Gov + B2B). First generation costs
 * 10 credits; the result persists on the lead (ai_strategy_notes) and
 * re-opens for free ("Ver Estratégia Salva").
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Trophy, Loader2, Copy, FileText, Target, Lightbulb, Mail,
  AlertTriangle, ChevronUp, Sparkles,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Section({ icon: Icon, color, title, text, copyKey, isPt }) {
  if (!text) return null;
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(isPt ? "Copiado!" : "Copied!");
    } catch (_e) { toast.error(isPt ? "Cópia bloqueada" : "Copy blocked"); }
  };
  return (
    <div className="rounded-lg border border-[var(--al-border,#E5E7EB)] p-3 bg-[var(--al-bg,transparent)]">
      <div className="flex items-center gap-1.5 mb-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color }} />
        <span className="text-[10.5px] uppercase tracking-wide font-extrabold" style={{ color }}>{title}</span>
        <button type="button" onClick={copy} data-testid={`ws-copy-${copyKey}`}
          title={isPt ? "Copiar" : "Copy"}
          className="ml-auto p-1 rounded hover:bg-black/5 text-[var(--al-text-muted,#6B7280)]">
          <Copy className="w-3 h-3" />
        </button>
      </div>
      <p className="text-[12px] leading-relaxed whitespace-pre-line">{text}</p>
    </div>
  );
}

export default function WinningStrategy({ leadType, leadId, isPt = true }) {
  const [strategy, setStrategy] = useState(null);
  const [checked, setChecked] = useState(false);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setStrategy(null); setOpen(false); setChecked(false);
    if (!leadId) return;
    axios.get(`${API}/winning-strategy/${leadType}/${leadId}`)
      .then(({ data }) => { if (data.exists) setStrategy(data.strategy); })
      .catch(() => {})
      .finally(() => setChecked(true));
  }, [leadType, leadId]);

  if (!leadId) return null;

  const generate = async () => {
    if (strategy) { setOpen((o) => !o); return; }
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/winning-strategy/generate`, {
        lead_type: leadType, lead_id: leadId, lang: isPt ? "pt" : "en",
      });
      setStrategy(data.strategy);
      setOpen(true);
      if (data.charged) {
        toast.success(isPt
          ? "🏆 Estratégia gerada e salva no lead (−10 créditos). Releitura é grátis!"
          : "🏆 Strategy generated and saved to the lead (−10 credits). Re-reads are free!");
      }
    } catch (e) {
      const msg = e?.response?.status === 402
        ? (isPt ? "Créditos insuficientes (custa 10) — recarregue para gerar." : "Insufficient credits (costs 10) — top up to generate.")
        : (e?.response?.data?.detail || "Strategy failed");
      toast.error(msg);
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-2" data-testid="winning-strategy">
      <button type="button" onClick={generate} disabled={busy || !checked}
        data-testid="ws-button"
        className={`w-full h-11 rounded-xl text-[12.5px] font-extrabold inline-flex items-center justify-center gap-2 transition-all disabled:opacity-60 ${
          strategy
            ? "border-2 border-[#8B5CF6] text-[#8B5CF6] hover:bg-[#8B5CF6]/10"
            : "bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white shadow-lg shadow-[#8B5CF6]/30 hover:brightness-110"}`}>
        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trophy className="w-4 h-4" />}
        {busy
          ? (isPt ? "Claire analisando como consultora sênior..." : "Claire analyzing as a senior consultant...")
          : strategy
            ? (open
              ? (isPt ? "Ocultar Estratégia" : "Hide Strategy")
              : (isPt ? "Ver Estratégia Salva" : "View Saved Strategy"))
            : (isPt ? "Gerar Estratégia de Vitória" : "Generate Winning Strategy")}
        {!strategy && !busy && (
          <span className="text-[9.5px] font-bold px-1.5 py-0.5 rounded bg-white/20">10 {isPt ? "créditos" : "credits"}</span>
        )}
        {strategy && !busy && (open ? <ChevronUp className="w-3.5 h-3.5" /> : null)}
      </button>

      {open && strategy && (
        <div className="rounded-xl border-2 border-[#8B5CF6]/40 overflow-hidden" data-testid="ws-panel">
          <div className="px-3.5 py-2.5 bg-gradient-to-r from-[#8B5CF6] to-[#6D28D9] text-white flex items-center gap-2">
            <Sparkles className="w-3.5 h-3.5" />
            <span className="text-[11.5px] font-extrabold flex-1">
              {isPt ? "Claire's Winning Strategy" : "Claire's Winning Strategy"}
            </span>
            <span className="text-[9px] text-white/70">
              {isPt ? "Salva no lead" : "Saved on lead"} · {String(strategy.generated_at || "").slice(0, 10)}
            </span>
          </div>
          <div className="p-3 space-y-2.5 bg-[var(--al-card-bg,#fff)]">
            <Section icon={FileText} color="#3B82F6" copyKey="summary" isPt={isPt}
              title={isPt ? "Resumo Executivo" : "Executive Summary"}
              text={strategy.executive_summary} />
            <Section icon={Target} color="#8B5CF6" copyKey="keypoint" isPt={isPt}
              title={isPt ? "Ponto Chave" : "Key Point"}
              text={strategy.key_point} />
            <Section icon={Lightbulb} color="#C9A24E" copyKey="tactic" isPt={isPt}
              title={isPt ? "Tática de Venda (seu diferencial)" : "Sales Tactic (your edge)"}
              text={strategy.sales_tactic} />
            <Section icon={Mail} color="#10B981" copyKey="email" isPt={isPt}
              title={isPt ? "Rascunho de E-mail Persuasivo" : "Persuasive Email Draft"}
              text={`${strategy.email_subject ? `${isPt ? "Assunto" : "Subject"}: ${strategy.email_subject}\n\n` : ""}${strategy.email_body}`} />
            <Section icon={AlertTriangle} color="#EF4444" copyKey="risk" isPt={isPt}
              title={isPt ? "Alerta de Risco" : "Risk Alert"}
              text={strategy.risk_alert} />
          </div>
        </div>
      )}
    </div>
  );
}
